import {i18n} from "../../shared/i18n";
// Контракт движка referral-v2. Бэкенд уже в проде, поэтому здесь только то,
// что подтверждено его валидацией — ошибки ниже это его коды ответа 400.
export const POLICY_LEVELS = [1, 2, 3, 4, 5, 6, 7];

export const POLICY_STATUSES = ["draft", "active", "retired"];

export const ROUNDING_MODES = ["floor", "ceil", "half_up"];

// target_id легко перепутать: у каждого скоупа своя сущность.
export const SCOPE_TYPES = [
    {value: "global", get label() { return i18n.t("sections.referralPolicies.p56"); }, target: "none", get hint() { return i18n.t("sections.referralPolicies.p57"); }},
    {value: "country", get label() { return i18n.t("sections.referralPolicies.p58"); }, target: "id", get hint() { return i18n.t("sections.referralPolicies.p59"); }},
    {value: "role", get label() { return i18n.t("sections.referralPolicies.p13"); }, target: "role", get hint() { return i18n.t("sections.referralPolicies.p60"); }},
    {value: "company", get label() { return i18n.t("sections.referralPolicies.p61"); }, target: "id", get hint() { return i18n.t("sections.referralPolicies.p62"); }},
    {value: "place", get label() { return i18n.t("sections.referralPolicies.p63"); }, target: "id", get hint() { return i18n.t("sections.referralPolicies.p64"); }},
    {value: "branch", get label() { return i18n.t("sections.referralPolicies.p65"); }, target: "id", get hint() { return i18n.t("sections.referralPolicies.p66"); }},
    {value: "user", get label() { return i18n.t("sections.referralPolicies.p67"); }, target: "id", get hint() { return i18n.t("sections.referralPolicies.p68"); }},
];

export const scopeType = (value) => SCOPE_TYPES.find((scope) => scope.value === value) || null;

export const emptyPolicyRules = () =>
    POLICY_LEVELS.map((level) => ({level, percent: "", enabled: true}));

const positiveId = (value) => /^[1-9]\d*$/.test(String(value ?? "").trim());

// Бэкенд отвечает percent_out_of_range — ловим до запроса.
const validPercent = (value) => {
    const text = String(value ?? "").trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return false;
    const parsed = Number(text);
    return parsed >= 0 && parsed <= 100;
};

export const validatePolicyDraft = ({
    name,
    scopeType: scope,
    targetId,
    role,
    effectiveFrom,
    roundingMode,
    rules,
}) => {
    const errors = {};
    const definition = scopeType(scope);

    if (!String(name ?? "").trim()) errors.name = i18n.t("sections.referralPolicies.p69");
    if (!definition) errors.scopeType = i18n.t("sections.referralPolicies.p70");
    if (!String(effectiveFrom ?? "").trim()) errors.effectiveFrom = i18n.t("sections.referralPolicies.p71");
    if (!ROUNDING_MODES.includes(roundingMode)) errors.roundingMode = i18n.t("sections.referralPolicies.p72");

    if (definition?.target === "id" && !positiveId(targetId)) {
        errors.targetId = i18n.t("sections.referralPolicies.p73", {v0: definition.hint.toLowerCase()});
    }
    if (definition?.target === "role" && !String(role ?? "").trim()) {
        errors.role = i18n.t("sections.referralPolicies.p74");
    }

    // Форма всегда шлёт 7 строк, иначе бэкенд отвечает seven_level_rules_required.
    const list = Array.isArray(rules) ? rules : [];
    if (list.length !== POLICY_LEVELS.length) {
        errors.rules = i18n.t("sections.referralPolicies.p75");
    } else {
        const levels = list.map((rule) => Number(rule.level));
        if (levels.join(",") !== POLICY_LEVELS.join(",")) {
            errors.rules = i18n.t("sections.referralPolicies.p76");
        } else {
            const bad = list.filter((rule) => !validPercent(rule.percent));
            if (bad.length) {
                errors.rules = i18n.t("sections.referralPolicies.p77", {v0: bad.map((rule) => rule.level).join(", ")});
            }
        }
    }

    return errors;
};

export const buildPolicyPayload = ({
    tenantId,
    policyId,
    name,
    scopeType: scope,
    targetId,
    role,
    effectiveFrom,
    roundingMode,
    rules,
}) => {
    const errors = validatePolicyDraft({
        name,
        scopeType: scope,
        targetId,
        role,
        effectiveFrom,
        roundingMode,
        rules,
    });
    if (Object.keys(errors).length) throw new Error(Object.values(errors)[0]);
    if (!positiveId(tenantId)) throw new Error(i18n.t("sections.referralPolicies.p54"));

    const definition = scopeType(scope);
    const payload = {
        tenant_id: Number(tenantId),
        name: String(name).trim(),
        scope_type: scope,
        effective_from: String(effectiveFrom).trim(),
        rounding_mode: roundingMode,
        rules: rules.map((rule) => ({
            level: Number(rule.level),
            percent: Number(rule.percent),
            // «Выключить уровень» — это enabled: false, а не отсутствие строки.
            enabled: Boolean(rule.enabled),
        })),
    };

    if (policyId) payload.policy_id = policyId;
    if (definition.target === "id") payload.target_id = Number(String(targetId).trim());
    if (definition.target === "role") payload.role = String(role).trim();

    return payload;
};

const versionNumber = (version) => Number(version?.version) || 0;

// Ручка отдаёт плоский список версий. Группируем по policy_id: активная сверху,
// остальные — история. Активную редактировать нельзя, она иммутабельна.
export const groupPolicyVersions = (response) => {
    const list = Array.isArray(response) ? response : (response?.results || []);
    const byPolicy = new Map();

    list.forEach((version) => {
        const key = String(version?.policy_id ?? version?.id ?? "");
        if (!key) return;
        if (!byPolicy.has(key)) byPolicy.set(key, []);
        byPolicy.get(key).push(version);
    });

    return Array.from(byPolicy.entries()).map(([policyId, versions]) => {
        const sorted = versions.slice().sort((left, right) => versionNumber(right) - versionNumber(left));
        const active = sorted.find((version) => version.status === "active") || null;
        return {
            policyId,
            name: (active || sorted[0])?.name || policyId,
            scopeType: (active || sorted[0])?.scope_type || null,
            active,
            // Активная показывается отдельно, остальное — история версий.
            history: sorted.filter((version) => version !== active),
            versions: sorted,
        };
    });
};

export const canActivate = (version) => version?.status === "draft";

// «На уровне N — X человек». Глубина уже считается flattenReferralNetwork,
// бэкенд для этого не нужен.
export const countByReferralLevel = (rows) => {
    const counts = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const level = Number(row?.level);
        if (!Number.isInteger(level) || level < 1) return;
        counts.set(level, (counts.get(level) || 0) + 1);
    });

    const deepest = counts.size ? Math.max(...counts.keys()) : 0;
    const levels = Math.max(POLICY_LEVELS.length, deepest);

    return Array.from({length: levels}, (_unused, index) => ({
        level: index + 1,
        count: counts.get(index + 1) || 0,
    }));
};
